import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Combobox, Listbox, BlockStack, InlineStack, Text, Icon, LegacyStack, Tag, Box, AutoSelection } from '@shopify/polaris';
import { SearchIcon, PlusCircleIcon } from '@shopify/polaris-icons';

import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function DiscountTagsPickerCustom(props) {
    const navigate = useHostNavigate();
    const fetch = FetchCustom();

    const [loading, setLoading] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState('');
    const [list, setList] = useState([]);
    const [selectedTags, setSelectedTags] = useState(props.value ? JSON.parse(props.value) : []);
    const [value, setValue] = useState('');
    const [suggestion, setSuggestion] = useState('');

    const load = async () => {
        setLoading(true);

        const data = await fetch('api/home/discountTags', {});
        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setList(data);
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setSelectedTags(props.value ? JSON.parse(props.value) : []);
    }, [props.value]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    const handleActiveOptionChange = useCallback(
        (activeOption) => {
            const activeOptionIsAction = activeOption === value;

            if (!activeOptionIsAction && !selectedTags.includes(activeOption)) {
                setSuggestion(activeOption);
            } else {
                setSuggestion('');
            }
        },
        [value, selectedTags],
    );

    const updateSelection = useCallback(
        (selected) => {
            const nextSelectedTags = new Set([...selectedTags]);

            selected.split(',').map(tag => tag.trim()).filter(Boolean)
                .forEach(tag => {
                    const existing = [...nextSelectedTags].find(
                        t => t.toLowerCase() === tag.toLowerCase()
                    );

                    if (existing) {
                        nextSelectedTags.delete(existing);
                    } else {
                        nextSelectedTags.add(tag);
                    }
                });

            const stags = [...nextSelectedTags];

            setSelectedTags(stags);
            setValue('');
            setSuggestion('');

            if (props.handleError && props.error && stags.length > 0) {
                props.handleError(props.fieldName, false);
            }

            props.handleChange(props.fieldName, stags.length ? JSON.stringify(stags) : null);
        },
        [selectedTags, props],
    );

    const removeTag = useCallback(
        (tag) => () => {
            updateSelection(tag);
        },
        [updateSelection],
    );

    const getAllTags = useCallback(() => {
        return [...new Set([...list, ...selectedTags])];
    }, [list, selectedTags]);

    const formatOptionText = useCallback(
        (option) => {
            const trimValue = value.trim().toLocaleLowerCase();
            const matchIndex = option.toLocaleLowerCase().indexOf(trimValue);

            if (!value || matchIndex === -1) return option;

            const start = option.slice(0, matchIndex);
            const highlight = option.slice(matchIndex, matchIndex + trimValue.length);
            const end = option.slice(matchIndex + trimValue.length, option.length);

            return (
                <p>
                    {start}
                    <Text fontWeight="bold" as="span">
                        {highlight}
                    </Text>
                    {end}
                </p>
            );
        },
        [value],
    );

    const escapeSpecialRegExCharacters = useCallback(
        (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
        [],
    );

    const options = useMemo(() => {
        let list;
        const allTags = getAllTags();
        const filterRegex = new RegExp(escapeSpecialRegExCharacters(value), 'i');

        if (value) {
            list = allTags.filter((tag) => tag.match(filterRegex));
        } else {
            list = allTags;
        }

        return [...list];
    }, [value, getAllTags, escapeSpecialRegExCharacters]);

    const allowAddNew = props.allowAddNew ?? true;

    const hasExactMatch = getAllTags().some(
        tag => tag.toLowerCase() === value.trim().toLowerCase()
    );

    const noResults = value.trim() && options.length === 0;

    const showAddAction = allowAddNew && value.trim() && !hasExactMatch;

    const optionMarkup =
        options.length > 0
            ? options.map((option, index) => {
                return (
                    <Listbox.Option
                        key={option}
                        value={option}
                        selected={selectedTags.some(tag => tag.toLowerCase() === option.toLowerCase())}
                        accessibilityLabel={option}
                        divider={showAddAction && index === options.length - 1}
                    >
                        <Listbox.TextOption selected={selectedTags.some(tag => tag.toLowerCase() === option.toLowerCase())}>
                            {formatOptionText(option)}
                        </Listbox.TextOption>
                    </Listbox.Option>
                );
            })
            : null;

    const actionMarkup = showAddAction ? (
        <Listbox.Action value={value}>
            <LegacyStack spacing="tight">
                <Icon source={PlusCircleIcon} tone="base" />
                <div>{`Add "${value}"`}</div>
            </LegacyStack>
        </Listbox.Action>
    ) : null;

    const listboxMarkup = loading ? (
        <Listbox autoSelection={AutoSelection.None}>
            <Listbox.Loading accessibilityLabel="Loading" />
        </Listbox>
    ) : optionMarkup || actionMarkup ? (
        <Listbox
            autoSelection={AutoSelection.None}
            onSelect={updateSelection}
            onActiveOptionChange={handleActiveOptionChange}
        >
            {optionMarkup}
            {actionMarkup}
        </Listbox>
    ) : noResults ? (
        <Box paddingInlineStart="400" paddingBlock="200">
            <Text as="p" tone="subdued">
                {`No tags found matching "${value}"`}
            </Text>
        </Box>
    ) : null;

    return (
        <BlockStack gap="300">
            <Combobox
                label={'Can combine with discounts tagged'}
                allowMultiple
                activator={
                    <Combobox.TextField
                        autoComplete="off"
                        label={props.label}
                        labelHidden={!props.label}
                        prefix={<Icon source={SearchIcon} tone="subdued" />}
                        error={props.error}
                        value={value}
                        suggestion={suggestion}
                        placeholder={props.placeholder}
                        onChange={setValue}
                    />
                }
            >
                {listboxMarkup}
            </Combobox>
            {
                selectedTags.length ?
                    <InlineStack gap="100">
                        {
                            selectedTags.map((tag) => (
                                <Tag key={`option-${tag}`} onRemove={removeTag(tag)}>
                                    {tag}
                                </Tag>
                            ))
                        }
                    </InlineStack>
                    : null
            }
            {
                props.helpText ?
                    <Text as="p" tone="subdued">{props.helpText}</Text>
                    : null
            }

        </BlockStack>
    );
}